import { c as _c } from "react/compiler-runtime";
import { useConfirmationDialog } from '@onefootprint/ui';
import { useEffect, useRef, useState } from 'react';
import { useStore } from '@/components/playbook-builder';
import useAdoptEditSession from './use-adopt-edit-session';

/**
 * Resolves a deep-linked edit session (`?editSessionId=`) into the container's two render decisions. The
 * session must be adopted before we can tell whether it's a draft, so this adopts up front and derives
 * `isDraft` (render the store-driven editor) and `committedEnabled` (whether `usePlaybook` should fetch).
 */
const useDeepLinkedDraft = (playbookId, editSessionId) => {
  const $ = _c(10);
  const store = useStore();
  const confirm = useConfirmationDialog();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const adoptEditSession = useAdoptEditSession(t0);
  const adoptedRef = useRef(null);
  const [adoptFailed, setAdoptFailed] = useState(false);
  let t1;
  if ($[1] !== adoptEditSession || $[2] !== confirm || $[3] !== editSessionId) {
    t1 = () => {
      if (!editSessionId || adoptedRef.current === editSessionId) {
        return;
      }
      adoptedRef.current = editSessionId;
      setAdoptFailed(false);
      confirm.hide();
      adoptEditSession(editSessionId).then(result => {
        if (!result) {
          setAdoptFailed(true);
        }
      });
    };
    $[1] = adoptEditSession;
    $[2] = confirm;
    $[3] = editSessionId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  let t2;
  if ($[5] !== editSessionId) {
    t2 = [editSessionId];
    $[5] = editSessionId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  useEffect(t1, t2);
  const sessionReady = !!store.data.sessionId && store.data.playbookId === playbookId;
  const isDraft = !!editSessionId && sessionReady && store.data.isDraft;
  const committedEnabled = !editSessionId || adoptFailed || sessionReady && !store.data.isDraft;
  let t3;
  if ($[7] !== committedEnabled || $[8] !== isDraft) {
    t3 = {
      isDraft,
      committedEnabled
    };
    $[7] = committedEnabled;
    $[8] = isDraft;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  return t3;
};
export default useDeepLinkedDraft;